/**
 * The catalogue for the "why" of a price step and a transfer (bata-ui#166 W4-2,
 * `docs/adr/6126`; bata-platform ADR 6126/6127).
 *
 * Every code the platform publishes on those two reads is a STABLE code the UI localises here.
 * A code this build does not know is shown by its raw value under an "unrecognised" label — the
 * platform can add one without a release (G5), and a plausible wrong name would be a lie about
 * why the step or the move is what it is.
 */

import type { PriceStepExplanationSummary, SolvedPathWeek } from '@/api/generated';

// ── price-step drivers ──────────────────────────────────────────────────────

/** How a driver's magnitude is read: money in the step's currency, units, or a count of actions. */
export type DriverUnit = 'money' | 'units' | 'actions';

interface DriverPresentation {
  readonly labelKey: string;
  readonly unit: DriverUnit;
}

/** `planning.markdown.optimise.drivers` — the four terms of the solved path's value. */
const PRICE_DRIVER: Readonly<Record<string, DriverPresentation>> = {
  margin_recovered: { labelKey: 'inseason.priceWhy.driver.marginRecovered', unit: 'money' },
  carrying_cost_avoided: { labelKey: 'inseason.priceWhy.driver.carryingCostAvoided', unit: 'money' },
  terminal_stock_headroom: { labelKey: 'inseason.priceWhy.driver.terminalStockHeadroom', unit: 'units' },
  markdown_actions: { labelKey: 'inseason.priceWhy.driver.markdownActions', unit: 'actions' },
};

export const UNRECOGNISED_PRICE_DRIVER_KEY = 'inseason.priceWhy.driver.unrecognised';

/** The label and unit of one published driver code; an unknown code keeps its raw value. */
export function priceDriverPresentation(code: string): DriverPresentation & { readonly known: boolean } {
  const known = PRICE_DRIVER[code];
  return known === undefined
    ? { labelKey: UNRECOGNISED_PRICE_DRIVER_KEY, unit: 'units', known: false }
    : { ...known, known: true };
}

// ── constraints ─────────────────────────────────────────────────────────────

/** `planning.markdown.explain`'s constraint codes, step-level and path-level. */
const PRICE_CONSTRAINT_KEY: Readonly<Record<string, string>> = {
  minimum_weeks_on_sale: 'inseason.priceWhy.constraint.minimumWeeksOnSale',
  minimum_weeks_between_steps: 'inseason.priceWhy.constraint.minimumWeeksBetweenSteps',
  maximum_single_step_depth: 'inseason.priceWhy.constraint.maximumSingleStepDepth',
  maximum_single_step_rungs: 'inseason.priceWhy.constraint.maximumSingleStepRungs',
  ladder_floor: 'inseason.priceWhy.constraint.ladderFloor',
  terminal_target: 'inseason.priceWhy.constraint.terminalTarget',
};

export const UNRECOGNISED_PRICE_CONSTRAINT_KEY = 'inseason.priceWhy.constraint.unrecognised';

export function priceConstraintKey(code: string): string {
  return PRICE_CONSTRAINT_KEY[code] ?? UNRECOGNISED_PRICE_CONSTRAINT_KEY;
}

/**
 * What the platform says bound one step, as a statement the panel renders:
 * `unrecorded` — the run kept no record (older than ADR 6126);
 * `free` — recorded, and no guardrail bound it: the optimiser's own choice;
 * `bound` — the codes that did.
 */
export type StepBinding =
  | { readonly kind: 'unrecorded' }
  | { readonly kind: 'free' }
  | { readonly kind: 'bound'; readonly codes: readonly string[] };

export function stepBinding(explanation: Pick<PriceStepExplanationSummary, 'step_constraints'>): StepBinding {
  const codes = explanation.step_constraints;
  if (codes === null || codes === undefined) return { kind: 'unrecorded' };
  return codes.length === 0 ? { kind: 'free' } : { kind: 'bound', codes };
}

// ── absence reasons ─────────────────────────────────────────────────────────

const WHY_REASON_KEY: Readonly<Record<string, string>> = {
  not_recorded_by_run: 'inseason.why.reason.notRecordedByRun',
  not_in_force: 'inseason.why.reason.notInForce',
  estimate_not_found: 'inseason.why.reason.estimateNotFound',
  position_not_recorded_by_run: 'inseason.why.reason.positionNotRecordedByRun',
  move_cap_not_recorded: 'inseason.why.reason.moveCapNotRecorded',
  // bata-platform ADR 6129 — the transfer-explanations report's reasons (`EXPLANATION_ABSENT_REASONS`),
  // and the transfers report's two of its own (ADR 9018): every code the transfers Why workbook can
  // carry, so its «Tecnico» sheet says each in words (DEC-104).
  donor_sells_nothing: 'inseason.why.reason.donorSellsNothing',
  recipient_profile_not_in_request: 'inseason.why.reason.recipientProfileNotInRequest',
  on_hand_not_measured: 'inseason.why.reason.onHandNotMeasured',
  committed_not_measured: 'inseason.why.reason.committedNotMeasured',
  rate_not_recorded: 'inseason.why.reason.rateNotRecorded',
  cover_not_recorded: 'inseason.why.reason.coverNotRecorded',
  position_not_bound: 'inseason.why.reason.positionNotBound',
  not_reported: 'inseason.why.reason.notReported',
  not_ranged: 'inseason.why.reason.notRanged',
  pending: 'inseason.why.reason.pending',
  superseded: 'inseason.why.reason.superseded',
  not_an_adjust: 'inseason.why.reason.notAnAdjust',
  not_superseded: 'inseason.why.reason.notSuperseded',
  sales_window_not_landed: 'inseason.why.reason.salesWindowNotLanded',
  not_published: 'inseason.why.reason.notPublished',
  // The rest of bata-platform's report absence reasons (`inseason_report.ABSENT_REASONS`), so no
  // code reaches a sheet or a cell as the raw code: `deferred` is a line whose decision was postponed.
  deferred: 'inseason.why.reason.deferred',
  store_not_in_location_master: 'inseason.why.reason.storeNotInLocationMaster',
  stock_batch_not_recorded_by_run: 'inseason.why.reason.stockBatchNotRecordedByRun',
  position_not_in_stock_batch: 'inseason.why.reason.positionNotInStockBatch',
  transit_not_reported: 'inseason.why.reason.transitNotReported',
  as_of_not_recorded_by_run: 'inseason.why.reason.asOfNotRecordedByRun',
  size_key_not_translatable: 'inseason.why.reason.sizeKeyNotTranslatable',
  calendar_not_landed: 'inseason.why.reason.calendarNotLanded',
  recovery_week_not_covered: 'inseason.why.reason.recoveryWeekNotCovered',
  recovery_cell_not_recovered: 'inseason.why.reason.recoveryCellNotRecovered',
  attributes_not_landed: 'inseason.why.reason.attributesNotLanded',
  brand_not_reported: 'inseason.why.reason.brandNotReported',
  unclassified: 'inseason.why.reason.unclassified',
  // bata-platform ADR 6130 — why the transfers report has no unit cost or no retail price (DEC-106);
  // the run's `as_of` reason, which both share with the sales windows, is above.
  landed_cost_not_landed: 'inseason.why.reason.landedCostNotLanded',
  landed_cost_not_in_force: 'inseason.why.reason.landedCostNotInForce',
  cost_currency_not_reported: 'inseason.why.reason.costCurrencyNotReported',
  retail_price_not_landed: 'inseason.why.reason.retailPriceNotLanded',
  retail_price_not_in_force: 'inseason.why.reason.retailPriceNotInForce',
  retail_price_currency_not_reported: 'inseason.why.reason.retailPriceCurrencyNotReported',
  retail_price_vat_basis_not_reported: 'inseason.why.reason.retailPriceVatBasisNotReported',
  retail_price_kind_not_reported: 'inseason.why.reason.retailPriceKindNotReported',
  // bata-platform ADR 9059 — the one run's lines report and its report by store: a held line decides nothing, a negative
  // on-hand row leaves its cell with no position, a store none of whose touched cells has a stock before, a gender the
  // attributes do not state; and this build's own: a line the report lacks, a shipment whose lines cannot be added.
  held_by_run: 'inseason.why.reason.heldByRun',
  on_hand_negative: 'inseason.why.reason.onHandNegative',
  no_touched_cell_has_before: 'inseason.why.reason.noTouchedCellHasBefore',
  gender_not_stated: 'inseason.why.reason.genderNotStated',
  not_in_report: 'inseason.why.reason.notInReport',
  values_not_additive: 'inseason.why.reason.valuesNotAdditive',
};

export const UNRECOGNISED_WHY_REASON_KEY = 'inseason.why.reason.unrecognised';

export function whyReasonKey(reason: string | null | undefined): string {
  return reason === null || reason === undefined
    ? UNRECOGNISED_WHY_REASON_KEY
    : WHY_REASON_KEY[reason] ?? UNRECOGNISED_WHY_REASON_KEY;
}

// ── guardrails ──────────────────────────────────────────────────────────────

interface GuardrailPresentation {
  readonly labelKey: string;
  readonly unitKey: string;
  readonly fractionDigits: number;
  /** A share the platform publishes as a fraction, shown as a percentage. */
  readonly percent: boolean;
}

/** The four markdown guardrails, by the registry key the run resolved them from. */
const GUARDRAIL: Readonly<Record<string, GuardrailPresentation>> = {
  'markdown.maximum_single_step_depth': {
    labelKey: 'inseason.priceWhy.guardrail.maximumSingleStepDepth', unitKey: 'inseason.priceWhy.unit.percent', fractionDigits: 0, percent: true,
  },
  'markdown.maximum_single_step_rungs': {
    labelKey: 'inseason.priceWhy.guardrail.maximumSingleStepRungs', unitKey: 'inseason.priceWhy.unit.rungs', fractionDigits: 0, percent: false,
  },
  'markdown.minimum_weeks_between_steps': {
    labelKey: 'inseason.priceWhy.guardrail.minimumWeeksBetweenSteps', unitKey: 'inseason.priceWhy.unit.weeks', fractionDigits: 0, percent: false,
  },
  'markdown.minimum_weeks_on_sale': {
    labelKey: 'inseason.priceWhy.guardrail.minimumWeeksOnSale', unitKey: 'inseason.priceWhy.unit.weeks', fractionDigits: 0, percent: false,
  },
};

const UNRECOGNISED_GUARDRAIL: GuardrailPresentation = {
  labelKey: 'inseason.priceWhy.guardrail.unrecognised', unitKey: 'inseason.priceWhy.unit.value', fractionDigits: 2, percent: false,
};

export function guardrailPresentation(key: string): GuardrailPresentation {
  return GUARDRAIL[key] ?? UNRECOGNISED_GUARDRAIL;
}

/** A guardrail's value as the measure shows it: a share becomes a percentage, `null` stays absent. */
export function guardrailValue(key: string, value: number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  return guardrailPresentation(key).percent ? value * 100 : value;
}

// ── the path ────────────────────────────────────────────────────────────────

/** Whether a solved week moves the price: the platform marks it with a (possibly empty) list. */
export function weekMoves(week: Pick<SolvedPathWeek, 'binding_constraints'>): boolean {
  return week.binding_constraints !== null && week.binding_constraints !== undefined;
}
